import React from 'react';
import { Box, Image, keyframes, useColorMode } from '@chakra-ui/react';

/**
 * Brand logos from `/public/brand`.
 * - `mark` = icon only (sidebar collapsed / favicon-size)
 * - `full` = icon + UTKARSH wordmark + tagline
 *
 * Full logo has dark wordmark/tagline — on dark surfaces use a light plate
 * so it stays readable. Optional shimmer adds a soft shine sweep.
 */
/* Full-duration glide — shine travels slowly across the logo (not a quick flash) */
const shimmerSweep = keyframes`
  0% { transform: translateX(-120%) skewX(-12deg); }
  100% { transform: translateX(120%) skewX(-12deg); }
`;

const softGlow = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 rgba(249, 177, 1, 0); }
  50% { box-shadow: 0 0 12px 0 rgba(249, 177, 1, 0.16); }
`;

export function BrandLogo({
  variant = 'full',
  h = variant === 'full' ? '40px' : '36px',
  maxW,
  /** auto = plate in dark mode (full only); true/false force */
  plate = 'auto',
  /** Soft shine sweep across the logo */
  shimmer = true,
  ...props
}) {
  const { colorMode } = useColorMode();
  const src =
    variant === 'mark'
      ? '/brand/app_icon.png'
      : '/brand/app_logo_full.png';
  const alt = variant === 'mark' ? 'Utkarsh' : 'Utkarsh — A Buyer Seller Portal';

  const usePlate =
    variant === 'full' &&
    (plate === true || (plate === 'auto' && colorMode === 'dark'));

  const image = (
    <Image
      src={src}
      alt={alt}
      h={h}
      maxW={maxW}
      w="auto"
      objectFit="contain"
      display="block"
      position="relative"
      zIndex={1}
    />
  );

  if (!usePlate && !shimmer) {
    return (
      <Box lineHeight={0} {...props}>
        {image}
      </Box>
    );
  }

  return (
    <Box
      position="relative"
      lineHeight={0}
      display="inline-flex"
      alignItems="center"
      justifyContent="center"
      overflow="hidden"
      borderRadius={usePlate ? 'md' : 'sm'}
      bg={usePlate ? 'white' : 'transparent'}
      px={usePlate ? 2 : 0}
      py={usePlate ? 1.5 : 0}
      borderWidth={usePlate ? '1px' : 0}
      borderColor={usePlate ? 'blackAlpha.100' : 'transparent'}
      animation={usePlate && shimmer ? `${softGlow} 6s ease-in-out infinite` : undefined}
      sx={
        shimmer
          ? {
              '&::after': {
                content: '""',
                position: 'absolute',
                inset: '-25% -55%',
                zIndex: 2,
                pointerEvents: 'none',
                background:
                  'linear-gradient(100deg, transparent 40%, rgba(255,255,255,0.45) 50%, rgba(249,177,1,0.2) 54%, transparent 62%)',
                animation: `${shimmerSweep} 6s linear infinite`,
              },
            }
          : undefined
      }
      {...props}
    >
      {image}
    </Box>
  );
}
